/**
 * Tour step definitions for all pages.
 * Each page has its own set of steps that highlight key UI elements.
 */

export interface TourStep {
  /** CSS selector for the target element to highlight */
  target: string;
  /** Title shown in the tooltip */
  title: string;
  /** Description of what this element does */
  content: string;
  /** Position of the tooltip relative to the target */
  placement: 'top' | 'bottom' | 'left' | 'right';
}

export interface PageTour {
  /** Route pattern this tour applies to */
  route: string;
  /** Friendly name for the page */
  pageName: string;
  /** Steps in order */
  steps: TourStep[];
}

export const PAGE_TOURS: PageTour[] = [
  {
    route: '/search/station',
    pageName: 'Station Search',
    steps: [
      {
        target: '.search-method-cards, .method-dropdown-trigger, .search-section',
        title: 'Search Method',
        content: 'Switch between Station Code, Location, and Country search to find weather stations.',
        placement: 'bottom',
      },
      {
        target: '.floating-search-bar, .unified-station-search, .station-code-search',
        title: 'Search Bar',
        content: 'Type a station code, WMO/ICAO ID, or name. Results appear on the map and in the table.',
        placement: 'bottom',
      },
      {
        target: '.map-view-wrapper',
        title: 'Interactive Map',
        content: 'Stations appear as markers. Click any marker to view station details and observations.',
        placement: 'left',
      },
      {
        target: '.table-view, .results-table',
        title: 'Results Table',
        content: 'All matching stations listed here. Click a row to highlight it on the map.',
        placement: 'top',
      },
    ],
  },
  {
    route: '/search/coordinates',
    pageName: 'Location Search',
    steps: [
      {
        target: '.floating-coordinates-search, .floating-search-bar',
        title: 'Coordinates Input',
        content: 'Enter latitude, longitude, and radius to find all stations in that area.',
        placement: 'bottom',
      },
      {
        target: '.map-view-wrapper',
        title: 'Search Radius',
        content: 'A circle shows your search area. Stations within the radius appear as markers.',
        placement: 'left',
      },
    ],
  },
  {
    route: '/search/country',
    pageName: 'Country Search',
    steps: [
      {
        target: '.floating-country-search, .floating-search-bar',
        title: 'Location Selector',
        content: 'Pick a country, then optionally narrow by state and city to find stations.',
        placement: 'bottom',
      },
      {
        target: '.map-view-wrapper',
        title: 'Boundary Overlay',
        content: 'Country and state boundaries are drawn on the map for your selection.',
        placement: 'left',
      },
    ],
  },
  {
    route: '/observations/CPTR3',
    pageName: 'Observations',
    steps: [
      {
        target: '.date-range-picker, .obs-date-picker, input[type="date"]',
        title: 'Date Range',
        content: 'Select start and end dates to view historical weather observations.',
        placement: 'bottom',
      },
      {
        target: '.view-toggle, .obs-view-toggle',
        title: 'View Mode',
        content: 'Switch between table view for raw data and charts for visual analysis.',
        placement: 'bottom',
      },
      {
        target: '.recharts-wrapper, .obs-chart-container',
        title: 'Time Series Chart',
        content: 'Visualize parameters over time. Hover for details, select parameters in the legend.',
        placement: 'top',
      },
      {
        target: '.export-btn, [aria-label*="export" i], [aria-label*="Export" i]',
        title: 'Export Data',
        content: 'Download observations as CSV, XLSX, or PDF for offline analysis.',
        placement: 'left',
      },
    ],
  },
  {
    route: '/observations',
    pageName: 'Observation Map',
    steps: [
      {
        target: '.mapboxgl-map, .observation-map',
        title: 'Observation Map',
        content: 'View real-time observations plotted on a map. Colors indicate parameter values.',
        placement: 'left',
      },
      {
        target: '.parameter-selector, .obs-parameter-select',
        title: 'Parameter Selection',
        content: 'Choose which weather parameter to visualize (temperature, wind, pressure, etc.).',
        placement: 'bottom',
      },
      {
        target: '.time-slider, .animation-controls',
        title: 'Time Slider & Animation',
        content: 'Scrub through time or press play to animate how observations change over hours.',
        placement: 'top',
      },
    ],
  },
  {
    route: '/availability',
    pageName: 'Availability',
    steps: [
      {
        target: '.availability-search-panel, .availability-search',
        title: 'Search Panel',
        content: 'Enter station codes, select parameters, and choose a year range to check data coverage.',
        placement: 'right',
      },
      {
        target: '.availability-results, .heatmap-card, .availability-display',
        title: 'Availability Heatmap',
        content: 'Green = data available, gaps = missing. Quickly spot coverage holes across years.',
        placement: 'top',
      },
    ],
  },
  {
    route: '/statistics',
    pageName: 'Statistics',
    steps: [
      {
        target: '.statistics-dashboard, .statistics-panel',
        title: 'Statistics Dashboard',
        content: 'Network-wide stats: station counts, geographic distribution, and data coverage metrics.',
        placement: 'top',
      },
    ],
  },
  {
    route: '/station-quality',
    pageName: 'Station Quality',
    steps: [
      {
        target: '.quality-tabs, .tab-navigation',
        title: 'View Modes',
        content: 'Switch between Table View for raw QC data and Charts View for visual trends.',
        placement: 'bottom',
      },
      {
        target: '.quality-dashboard, .qc-dashboard',
        title: 'Quality Dashboard',
        content: 'Review QC flags, pass/fail rates, and identify stations with data quality issues.',
        placement: 'top',
      },
    ],
  },
];

/**
 * Find the tour for the current route.
 * Matches exact first, then prefix for dynamic routes like /observations/:code.
 */
export function getTourForRoute(pathname: string): PageTour | undefined {
  // Exact match first
  const exact = PAGE_TOURS.find(t => t.route === pathname);
  if (exact) return exact;

  // /observations/:code → use the observations tour
  if (pathname.startsWith('/observations/') && pathname !== '/observations/') {
    return PAGE_TOURS.find(t => t.route.startsWith('/observations/'));
  }

  // /search/:method prefix match
  return PAGE_TOURS.find(t => t.route !== '/observations' && pathname.startsWith(t.route));
}
